import { useState } from "react";

import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Dropdown,
  Modal,
  ModalProvider,
  Option,
  SecondaryButton,
  TextField,
  useNotifications,
} from "@joor/design-system";
import { useAuthPrincipal } from "AuthPrincipal";
import { useWorkspaceId } from "components/Workspace/Routes";
import {
  seasonComparator,
  toDropDownOption,
} from "Formatters/seasonFormatters";
import { containsAlphaNumeric } from "../utils";
import usePortal from "usePortal";
import { extractErrorReportFromApolloError } from "lib/Apollo";
import { ErrorReport } from "lib/Datadog";
import { getNodesFromEdges } from "lib/Relay";
import Mixpanel from "mixpanel-browser";
import {
  AccountSeasonsDoc,
  CreateAssortmentInWorkspaceDoc,
  WorkspaceOverviewDoc,
} from "operations";
import styled from "styled-components/macro";

type DropdownOption = { selected: Option };

const StyledModalContainer = styled.div`
  text-align: left;
  width: 504px;
  overflow: visible;
  label {
    display: block;
    margin-bottom: 16px;
  }
  header {
    display: flex;
    margin-bottom: 16px;
  }
`;
const StyledDropdown = styled(Dropdown)`
  line-height: 1em;
`;

const StyledModal = styled(Modal)`
  overflow: visible;
`;

const trackOpenCreateModal = () => {
  try {
    Mixpanel.track("Create Order in Workspace");
  } catch (e) {}
};

const trackSuccessfulTransaction = () => {
  try {
    Mixpanel.track("Complete Create Order in Workspace");
  } catch (e) {}
};

type CreateOrderProps = {
  isOpen: boolean;
  toggleModal: () => void;
};

const CreateOrderModal = ({ isOpen, toggleModal }: CreateOrderProps) => {
  const Portal = usePortal();
  const workspaceId = useWorkspaceId();
  const { loading, data } = useQuery(AccountSeasonsDoc);
  const { addToasts } = useNotifications();
  const principal = useAuthPrincipal();
  const dropdownSeasons = getNodesFromEdges(data?.seasons.edges)
    .sort(seasonComparator)
    .map(toDropDownOption);
  const [workspaceName, setWorkspaceName] = useState("");
  const [seasonId, setSeasonId] = useState("");
  const [createAssortmentMutation] = useMutation(
    CreateAssortmentInWorkspaceDoc
  );
  // Teardown logic for the modal
  const toggleCreateModal = () => {
    setWorkspaceName("");
    setSeasonId("");
    toggleModal();
  };

  const displayErrors = (errors: ErrorReport) => {
    addToasts([
      {
        title: "Failed To Create Order",
        description: `${errors.message})`,
        type: "error",
      },
    ]);
  };

  const displaySuccess = (orderName: string) => {
    addToasts([
      {
        title: "Order Created",
        description: `Order ${orderName} was successfully created.`,
        type: "success",
      },
    ]);
  };

  return (
    <>
      <SecondaryButton
        onClick={() => {
          toggleCreateModal();
          trackOpenCreateModal();
        }}
        data-testid="open-create-ws"
      >
        Create Order
      </SecondaryButton>
      <Portal>
        <ModalProvider>
          <StyledModal isOpen={isOpen} onClose={toggleCreateModal}>
            <StyledModalContainer>
              <Modal.Title iconName="add" iconSize="small">
                Create Order
              </Modal.Title>
              <TextField
                label={"Order Name"}
                placeholder={"Enter Order Name"}
                isRequired
                value={workspaceName}
                onChange={({ target: { value: newValue } }) => {
                  setWorkspaceName(newValue);
                }}
              />
              <StyledDropdown
                disabled={loading}
                isRequired
                label="Season"
                placeholder="Select a Season"
                options={dropdownSeasons}
                onSelect={(args: DropdownOption) =>
                  setSeasonId(args.selected.value)
                }
                testId="create-ws-season-dropdown"
              />
              <Modal.Actions>
                <Button
                  disabled={
                    !containsAlphaNumeric(workspaceName.trim()) || !seasonId
                  }
                  onClick={async () => {
                    const assortmentName = workspaceName.trim();
                    if (assortmentName !== "" && seasonId !== "" && principal) {
                      try {
                        const { data, errors } = await createAssortmentMutation(
                          {
                            variables: {
                              retailerId: principal.account.id,
                              assortmentName: assortmentName,
                              seasonId,
                              workspaceId,
                            },
                            refetchQueries: [
                              {
                                query: WorkspaceOverviewDoc,
                                variables: { workspaceId },
                              },
                            ],
                          }
                        );
                        if (errors) {
                          displayErrors(
                            extractErrorReportFromApolloError({
                              graphQLErrors: errors,
                            } as ApolloError)
                          );
                        } else {
                          displaySuccess(
                            data?.createAssortment.assortment?.name || ""
                          );
                        }
                        trackSuccessfulTransaction();
                      } catch (e) {
                        displayErrors(
                          extractErrorReportFromApolloError(e as ApolloError)
                        );
                      }
                      toggleCreateModal();
                    }
                  }}
                  data-testid="create-ws-button"
                >
                  Create Order
                </Button>
              </Modal.Actions>
            </StyledModalContainer>
          </StyledModal>
        </ModalProvider>
      </Portal>
    </>
  );
};

export default CreateOrderModal;
